import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { EditableTitle } from 'src/components/EditableTitle';
import { Heading } from 'src/components/Heading';
import { Loading } from 'src/components/Loading';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNavigation } from 'src/contexts/NavigationContext';
import { DateOverrides } from './components/DateOverrides';
import { days } from './constants';
import {
  ContactWindowProvider,
  useContactWindow,
} from './ContactWindowContext';
import { TimezoneSettings } from './TimezoneSettings';
import { WeeklyContactWindow } from './WeeklyContactWindow';

function ContactWindowContent() {
  const { navigateWithBlocking } = useNavigation();
  const {
    apply: applyContactWindow,
    description,
    label,
    setLabel,
    setDescription,
    selectedDays,
    isInitializing,
  } = useContactWindow();
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const { environmentData } = useEnvironment();
  const [searchParams] = useSearchParams();

  const campaignName = searchParams.get('campaignName');
  const campaignDescription = searchParams.get('campaignDescription');
  const returnTo = searchParams.get('returnTo');

  const hasCampaignDetails = useMemo(() => {
    return (
      (campaignName?.length ?? 0) > 0 && (campaignDescription?.length ?? 0) > 0
    );
  }, [campaignName, campaignDescription]);

  const searchParamsString = useMemo(() => {
    return new URLSearchParams({
      campaignName: campaignName ?? '',
      campaignDescription: campaignDescription ?? '',
    }).toString();
  }, [campaignName, campaignDescription]);

  const handleCancel = () =>
    navigateWithBlocking(
      returnTo
        ? returnTo
        : `/beta/campaigns${hasCampaignDetails ? `?${searchParamsString}` : ''}`,
    );
  const handleSaveExit = async () => {
    await applyContactWindow();
    navigateWithBlocking(
      returnTo
        ? returnTo
        : `/beta/campaigns${hasCampaignDetails ? `?${searchParamsString}` : ''}`,
    );
  };
  const handleApply = async () => {
    await applyContactWindow();
    navigateWithBlocking(
      returnTo
        ? returnTo
        : `/beta/campaigns${hasCampaignDetails ? `?${searchParamsString}` : ''}`,
    );
  };

  const selectedDaysText = useMemo(
    () =>
      days
        .filter((d) => selectedDays.includes(d.value))
        .map((d) => d.label)
        .join(', '),
    [selectedDays],
  );

  if (!environmentData || isInitializing) {
    return (
      <div className="flex gap-2 items-center justify-center py-12">
        <Loading size="8" />
        <p className="text-neutral-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <Heading
        title={
          <EditableTitle
            titleInputRef={titleInputRef}
            isEditing={isEditingDetails}
            setIsEditing={setIsEditingDetails}
            name={label}
            description={description}
            onNameChange={setLabel}
            onDescriptionChange={setDescription}
            titlePlaceholder="Contact window"
            subtitlePlaceholder="Add description"
          />
        }
        subtitle={selectedDaysText}
      >
        <div className="flex gap-2 mt-3">
          <Button variant="outlined" size="small" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="outlined" size="small" onClick={handleSaveExit}>
            Save & Exit
          </Button>
          <Button size="small" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </Heading>
      <ContentWrapper className="space-y-6">
        <WeeklyContactWindow />
        <TimezoneSettings />
        <DateOverrides />
      </ContentWrapper>
    </div>
  );
}

export default function ContactWindow() {
  return (
    <ContactWindowProvider>
      <ContactWindowContent />
    </ContactWindowProvider>
  );
}
